import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { extractDepthSlice, readFileForServer, iterationLabel, implausibleModelWarning } from '../lib/inversion-parser';
import { readInversionModel } from '../lib/inversion-read';
import { guessCrs } from '../lib/georef';
import { loadSample } from '../lib/sample-models';
import { useOptionalToast } from './Toast';
import { viewer3dPath } from '../lib/map-session';
import useModelGeoref, { modelBounds } from '../hooks/useModelGeoref';
import useStoredInversions from '../hooks/useStoredInversions';
import useDoiSettings from '../hooks/useDoiSettings';
import DemSurfaceToggle from './terrain/DemSurfaceToggle';
import {
  computeDoiProfile, makeDoiMask, DIFF_PROPS, DIVERGING_PAINT, isDivergingProp, divergingGeoJSON,
} from '../lib/inversion-results';
import useTimelapseDiff from '../hooks/useTimelapseDiff';
import TimelapseDateSlider from './TimelapseDateSlider';
import QCChartsModal from './QCChartsModal';
import ThemeMapPanel from './ThemeMapPanel';
import GeorefDialog from './GeorefDialog';
import SaveModelDialog from './SaveModelDialog';
import { useTheme } from '../contexts/ThemeContext';

const QC_PROPS = [
  { id: 'resistivity', label: 'Resistivity', log: true },
  { id: 'chargeability', label: 'Chargeability', log: false },
  { id: 'sensitivity', label: 'Coverage / Sensitivity', log: false },
  { id: 'misfit', label: 'Misfit', log: false },
];

/** Build a slice/QC GeoJSON with normalized values (log scale optional). */
function cellsToGeoJSON(cells, property, useLog) {
  const vals = cells.map(c => c.value ?? c[property]).filter(v => typeof v === 'number' && v > (useLog ? 0 : -Infinity));
  if (vals.length === 0) return null;
  const tf = useLog ? Math.log10 : (v) => v;
  let vmin = Infinity, vmax = -Infinity;
  vals.forEach(v => { const t = tf(v); if (t < vmin) vmin = t; if (t > vmax) vmax = t; });
  const span = vmax > vmin ? vmax - vmin : 1;
  return {
    type: 'FeatureCollection',
    features: cells.filter(c => typeof (c.value ?? c[property]) === 'number' && c.lon !== undefined).map(c => {
      const v = c.value ?? c[property];
      return {
        type: 'Feature',
        properties: {
          value: Math.round(v * 1e4) / 1e4,
          normalized: Math.round(((tf(v) - vmin) / span) * 1e4) / 1e4,
          depth: c.depth,
        },
        geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
      };
    }),
    metadata: {
      min: useLog ? Math.pow(10, vmin) : vmin,
      max: useLog ? Math.pow(10, vmax) : vmax,
      log: useLog, property,
    },
  };
}

/**
 * InversionPanel — import inversion results (EarthImager 1D/2D/3D .out/.mod/InvRes
 * via the backend registry; EarthImager .xyz/.dat and RES2DINV .inv in the browser),
 * georeference them (one GeoRef per model: a drawn survey line, or the
 * "define coordinates" dialog), depth slices, QC themes.
 *
 * Props:
 *   drawnLines - LineString features (Terra Draw); optional quick georeference
 *   models     - [{ id, name, model, cells (lon/lat once georeferenced), line, georef }]
 *   onAddModel(entry), onUpdateModel(entry)
 *   onSliceLayer(geojson|null, label) - show/replace the depth-slice map layer
 *   onAddQcLayer(geojson, name, kind?, extra?) - add a result layer (QC theme, electrodes, footprint);
 *                                      `extra` (e.g. { paint }) is merged into the layer
 *   onSliceLayer extra arg: onSliceLayer(geojson|null, label, extra?)
 *   onOpenSection(modelId)           - open the filled section view
 *   onOpenDataFit(entry)             - open the measured / calculated / misfit pseudosections
 *   onClose()
 *   projectId      - the map's project (?project=): imports are stored with it (V.12) and its
 *                    stored models are listed; without a project models stay in the browser
 *   datasetId      - dataset the map was opened from (?dataset=): linked on import
 *   initialModelId - stored model to load on open (?model=, "Open on map" on the dataset page)
 *
 * Stored entries carry `backendId` / `projectId`: georef edits are saved with PUT /georef,
 * theme maps and the time-lapse difference use the server; the browser copy (cells with
 * lon/lat) stays the cache for slices, DOI and sections.
 */
export default function InversionPanel({ drawnLines = [], models = [], onAddModel, onUpdateModel = () => {}, onFitBounds, onSliceLayer, onAddQcLayer, onOpenSection, onOpenDataFit, onClose, projectId = null, datasetId = null, initialModelId = null, navigate = null }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [notice, setNotice] = useState(null); // storage status (not saved / queued)
  const [pending, setPending] = useState(null); // parsed file waiting for the Save / View only answer
  const [lineIdx, setLineIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [sampleMenu, setSampleMenu] = useState(false);
  const toast = useOptionalToast();
  const [activeModelId, setActiveModelId] = useState(models[0]?.id || '');
  const [depthIdx, setDepthIdx] = useState(0);
  const [sliceProp, setSliceProp] = useState('resistivity');
  const [playing, setPlaying] = useState(false);
  const [showQcCharts, setShowQcCharts] = useState(false);
  const [showThemeMaps, setShowThemeMaps] = useState(false);
  const [compareId, setCompareId] = useState('');
  const [compareName, setCompareName] = useState('');
  const diff = useTimelapseDiff({
    onAddModel: (entry) => { onAddModel(entry); setActiveModelId(entry.id); setDepthIdx(0); setSliceProp(defaultProp(entry)); },
  });
  const playRef = useRef(null);
  const sliceShownRef = useRef(false);
  const doi = useDoiSettings();

  const active = models.find(m => m.id === activeModelId) || models[0];
  const depths = useMemo(() => active?.model?.depths || [], [active]);
  const isDiff = active?.model?.format === 'timelapse-diff';
  const availableProps = !active ? [] : isDiff
    ? DIFF_PROPS.filter(p => active.model.columns.includes(p.id)).map(p => ({ ...p, log: !p.diverging }))
    : QC_PROPS.filter(p => active.model.columns.includes(p.id));
  const defaultProp = (entry) => (entry?.model?.format === 'timelapse-diff' ? 'resistivity_pct_change' : 'resistivity');

  const activeIsLocal = !!active && active.cells?.[0]?.lon === undefined;
  const geo = useModelGeoref({ onUpdateModel, onAddLayer: onAddQcLayer, onFitBounds, onPersistError: setNotice });
  const stored = useStoredInversions({ projectId, datasetId });
  const loadedIds = new Set(models.map(m => m.backendId).filter(Boolean));

  /** Store a parsed file (POST /import); returns the storage result and sets the notice. */
  const storeModel = async (name, text, fileName, iteration, projectId = null) => {
    const saved = await stored.store({ name, text, fileName, iteration: iteration ?? null, projectId });
    setNotice(saved.status === 'stored' ? null : saved.message);
    return saved;
  };

  const loadStored = async (id, { select = true } = {}) => {
    const existing = models.find(m => m.backendId === id);
    if (existing) { if (select) setActiveModelId(existing.id); return existing; }
    const entry = await stored.load(id);
    onAddModel(entry);
    if (select) {
      setActiveModelId(entry.id); setDepthIdx(0); setSliceProp(defaultProp(entry));
      // A stored model comes back with its georeference: put its footprint and electrodes on the
      // map like a fresh import does (grouped under the model in the Layers panel).
      if (entry.georef) geo.addLayers(entry);
      const bounds = onFitBounds && modelBounds(entry);
      if (bounds) onFitBounds(bounds);
    }
    return entry;
  };

  const handleLoadStored = async (id) => {
    setError(null);
    setBusy(true);
    try { await loadStored(id); } catch (err) { setError(err?.response?.data?.detail || err.message); } finally { setBusy(false); }
  };

  const handleDeleteStored = async (item) => {
    if (!window.confirm(`Delete the stored model "${item.name}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await stored.remove(item.id);
      // A loaded copy stays in the panel as a browser-only model
      models.filter(m => m.backendId === item.id).forEach(m => onUpdateModel({ ...m, backendId: null, projectId: null }));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  // ?model=<id> ("Open on map" from the dataset page): load that stored model once
  const initialLoaded = useRef(false);
  const loadStoredRef = useRef(handleLoadStored);
  loadStoredRef.current = handleLoadStored;
  useEffect(() => {
    if (!initialModelId || initialLoaded.current) return;
    initialLoaded.current = true;
    loadStoredRef.current(initialModelId);
  }, [initialModelId]);

  const readModel = readInversionModel;

  // Import is two steps: parse the file, then ask whether to save it with a project
  // (SaveModelDialog); `finishImport` shows it either way.
  const handleFile = async (file) => {
    setError(null);
    setWarning(null);
    if (!file) return;
    setBusy(true);
    try {
      const text = await readFileForServer(file);
      const fileName = file.name.replace(/\.gz$/i, ''); // unzipped in the browser; detect by the inner name
      const model = await readModel(text, fileName);
      setPending({ text, fileName, model });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const finishImport = async ({ text, fileName, model }, projectId) => {
    setPending(null);
    setBusy(true);
    try {
      const warning = implausibleModelWarning(model, fileName);
      let entry = {
        id: `inv-${Date.now()}-${fileName}`,
        name: model.name,
        model, cells: model.cells, line: null, georef: null,
      };
      // Store it with the chosen project (V.12); the parsed copy stays as the display cache.
      // View only: nothing is stored and the notice says the model lives in this tab.
      const saved = projectId
        ? await storeModel(model.name, text, fileName, model.iteration, projectId)
        : { status: 'local', message: 'Not saved (view only). This model lives in this browser tab only.' };
      if (!projectId) setNotice(saved.message);
      if (saved.status === 'stored') entry = { ...entry, backendId: saved.id, projectId: saved.projectId, datasetId: saved.projectId === stored.projectId ? datasetId : null };
      geo.remember(entry.id, { text, fileName, iteration: model.iteration });
      setWarning(warning);
      onAddModel(entry);
      setActiveModelId(entry.id);
      setDepthIdx(0);
      const line = drawnLines[lineIdx];
      const inherited = saved.status === 'stored' && saved.meta?.georeferenced && saved.meta.georef;
      if (inherited) {
        // The server georeferenced it from the linked dataset's metadata.georef
        const updated = await geo.apply(entry, saved.meta.georef, { persist: false });
        const bounds = onFitBounds && modelBounds(updated);
        if (bounds) onFitBounds(bounds);
      } else if (line && model.xRange && model.xRange[1] > model.xRange[0]) {
        await geo.apply(entry, geo.fromDrawnLine(model, line));
      } else {
        geo.open(entry); // ask for start/end points, origin + azimuth, or tie points
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Load sample: a bundled demo as a session model (never stored), georeferenced with a fixed demo position.
  const handleLoadSample = async (id) => {
    setSampleMenu(false);
    setError(null);
    setWarning(null);
    setBusy(true);
    try {
      const entry = await loadSample(id);
      if (!models.some(m => m.id === entry.id)) onAddModel(entry);
      setActiveModelId(entry.id); setDepthIdx(0); setSliceProp(defaultProp(entry));
      geo.addLayers(entry);
      const bounds = onFitBounds && modelBounds(entry);
      if (bounds) onFitBounds(bounds);
      toast.showToast(`Loaded ${entry.name} (sample, not saved)`, 'success', 3500);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setError(msg);
      toast.showToast(`Could not load the sample: ${msg}`, 'error', 5000);
    } finally {
      setBusy(false);
    }
  };

  const changeIteration = async (n) => {
    const src = active && geo.sourceOf(active.id);
    if (!src) return;
    setBusy(true);
    setError(null);
    try {
      const model = await readModel(src.text, src.fileName, n);
      geo.remember(active.id, { ...src, iteration: n });
      let next = { ...active, model, cells: model.cells, line: null };
      if (active.backendId) {
        // The stored model follows the shown iteration: store it, then drop the old row
        const saved = await storeModel(active.name, src.text, src.fileName, n);
        if (saved.status === 'stored') {
          stored.remove(active.backendId).catch(() => {});
          next = { ...next, backendId: saved.id };
        } else {
          next = { ...next, backendId: null };
        }
      }
      if (active.georef) await geo.apply(next, active.georef);
      else onUpdateModel(next);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Global DOI mask: cells below the (sensitivity-based) DOI are left out of the slice
  const isBelowDoi = useMemo(
    () => makeDoiMask(active?.cells || [], { mask: doi.mask, threshold: doi.threshold, fallbackDoi: active?.model?.doi }),
    [active, doi.mask, doi.threshold],
  );
  const doiProfile = useMemo(
    () => (active ? computeDoiProfile(active.cells, doi.threshold, active.model.doi) : null),
    [active, doi.threshold],
  );
  const doiMean = doiProfile && doiProfile.columns.size
    ? [...doiProfile.columns.values()].reduce((s, v) => s + v, 0) / doiProfile.columns.size : null;

  const showSlice = useCallback((idx, prop) => {
    if (!active || active.cells?.[0]?.lon === undefined) return;
    const depth = depths[idx];
    if (depth === undefined) return;
    sliceShownRef.current = true;
    const sliceCells = extractDepthSlice(active.cells, depth, prop, null, isBelowDoi);
    const diverging = isDivergingProp(prop);
    const useLog = prop === 'resistivity' || prop === 'resistivity_a' || prop === 'resistivity_b';
    const geojson = diverging ? divergingGeoJSON(sliceCells, prop) : cellsToGeoJSON(sliceCells.map(s => ({ ...s })), 'value', useLog);
    if (geojson) onSliceLayer(geojson, `${active.name} · ${prop} @ ${depth}m`, { source: { kind: 'model', id: active.id, name: active.name }, ...(diverging ? { paint: DIVERGING_PAINT } : {}) });
  }, [active, depths, onSliceLayer, isBelowDoi]);

  // Re-draw a shown slice when the DOI mask or threshold changes
  const showSliceRef = useRef(showSlice);
  showSliceRef.current = showSlice;
  const depthIdxRef = useRef(depthIdx);
  depthIdxRef.current = depthIdx;
  const slicePropRef = useRef(sliceProp);
  slicePropRef.current = sliceProp;
  useEffect(() => {
    if (sliceShownRef.current) showSliceRef.current(depthIdxRef.current, slicePropRef.current);
  }, [isBelowDoi]);

  // Depth animation
  useEffect(() => {
    if (!playing || depths.length === 0) return;
    playRef.current = setInterval(() => {
      setDepthIdx(prev => {
        const next = (prev + 1) % depths.length;
        showSlice(next, sliceProp);
        return next;
      });
    }, 900);
    return () => clearInterval(playRef.current);
  }, [playing, depths.length, sliceProp, showSlice]);

  // Time-lapse: model B is a panel model or a stored model of the project (loaded on demand)
  const compareStored = stored.items.filter(i => !loadedIds.has(i.id) && i.format !== 'timelapse-diff');
  const runCompare = async () => {
    setError(null);
    try {
      const b = compareId.startsWith('stored:')
        ? await loadStored(compareId.slice('stored:'.length), { select: false })
        : models.find(m => m.id === compareId);
      if (!b) return;
      setCompareId(b.id);
      const entry = await diff.compare(active, b, compareName.trim() || `${b.name} − ${active.name}`);
      if (entry?.backendId) stored.refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const generateQcThemes = () => {
    if (!active) return;
    if (activeIsLocal) { setError('Define the coordinates of this model first'); return; }
    let count = 0;
    availableProps.forEach(p => {
      const diverging = isDivergingProp(p.id);
      const geojson = diverging
        ? divergingGeoJSON(active.cells.map(c => ({ lon: c.lon, lat: c.lat, value: c[p.id], depth: c.depth })), p.id)
        : cellsToGeoJSON(active.cells, p.id, p.log);
      if (geojson) {
        onAddQcLayer(geojson, `QC ${p.label} — ${active.name}`, 'grid', { source: { kind: 'model', id: active.id, name: active.name }, ...(diverging ? { paint: DIVERGING_PAINT } : {}) });
        count++;
      }
    });
    if (count === 0) setError('No QC properties found in this model');
  };

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };

  return (
    <div style={{ width: 265, background: C.bg2, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'auto', flexShrink: 0 }}>
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>⚡ Inversion results</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Import */}
        <div>
          <label style={lbl}>Georeference with</label>
          <select value={lineIdx} onChange={e => setLineIdx(parseInt(e.target.value))} style={selStyle}>
            <option value={-1}>Define coordinates after import</option>
            {drawnLines.map((_, i) => <option key={i} value={i}>Drawn line {i + 1} (x-range ends)</option>)}
          </select>
        </div>
        <label style={{ display: 'block', padding: '8px', borderRadius: 8, border: `1px dashed ${C.accentBorder}`, textAlign: 'center', cursor: busy ? 'wait' : 'pointer', fontSize: 11, color: C.accent }}>
          {busy ? '⏳ Reading…' : '📥 Import .out / .mod / .xyz / .inv / .dat / BERT .zip / 1D .json (.gz ok)'}
          <input type="file" accept=".out,.mod,.xyz,.inv,.dat,.txt,.csv,.zip,.json,.gz" style={{ display: 'none' }}
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} disabled={busy} />
        </label>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setSampleMenu(v => !v)} disabled={busy} aria-haspopup="menu" aria-expanded={sampleMenu} data-testid="inversion-load-sample"
            title="Show a bundled EarthImager demo on the map (not saved)"
            style={{ width: '100%', fontSize: 11, padding: '6px', borderRadius: 8, background: C.bg4, color: C.text1, border: `1px solid ${C.border}`, cursor: busy ? 'wait' : 'pointer' }}>
            Load sample ▾
          </button>
          {sampleMenu && (
            <div role="menu" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 2, background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
              {[['agi56dd', 'EarthImager 2D demo (AGI56DD trial 3)'], ['hill3d', 'EarthImager 3D demo (hill3d, terrain)']].map(([id, label]) => (
                <button key={id} role="menuitem" data-testid={`inversion-sample-${id}`} onClick={() => handleLoadSample(id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 11, padding: '6px 8px', background: 'none', border: 'none', color: C.text1, cursor: 'pointer' }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {warning && <div role="alert" data-testid="inversion-warning" style={{ fontSize: 10, color: C.amber, padding: '5px 8px', background: 'rgba(251,191,36,0.1)', borderRadius: 6 }}>⚠ {warning}</div>}
        {error && <div style={{ fontSize: 10, color: C.red, padding: '5px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {error}</div>}
        {notice && <div role="status" data-testid="inversion-store-notice" style={{ fontSize: 10, color: C.amber, padding: '5px 8px', background: 'rgba(251,191,36,0.1)', borderRadius: 6 }}>ⓘ {notice}</div>}

        {/* Stored models of the project (V.12) */}
        {stored.projectId && (
          <div data-testid="inversion-stored-list">
            <label style={lbl}>Stored models ({stored.items.length}){stored.loading ? ' — loading…' : ''}</label>
            {stored.error && <div style={{ fontSize: 10, color: C.red }}>✗ {stored.error}</div>}
            {!stored.loading && !stored.error && stored.items.length === 0 && (
              <div style={{ fontSize: 10, color: C.text2 }}>None yet — imports are stored with this project.</div>
            )}
            {stored.items.length > 0 && (
              <div style={{ maxHeight: 130, overflow: 'auto', border: `1px solid ${C.border}`, borderRadius: 6, padding: 4 }}>
                {stored.items.map(item => (
                  <div key={item.id} data-testid="inversion-stored-item" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: C.text1, padding: '2px 2px' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                      {item.name}{item.georeferenced ? '' : ' (local)'}
                    </span>
                    {active?.backendId === item.id
                      ? <span title="Active model" style={{ fontSize: 9, color: C.emerald }}>✓ loaded</span>
                      : loadedIds.has(item.id)
                      ? <button onClick={() => loadStored(item.id)} title="Make this the active model" style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.accent, cursor: 'pointer' }}>Select</button>
                      : <button onClick={() => handleLoadStored(item.id)} disabled={busy} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.accent, cursor: 'pointer' }}>Load</button>}
                    {navigate && (
                      <button onClick={() => navigate(viewer3dPath(stored.projectId, { model: item.id }))} title={`Open ${item.name} in the 3D viewer`} aria-label={`Open ${item.name} in 3D`}
                        style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: C.bg4, border: `1px solid ${C.border}`, color: C.accent, cursor: 'pointer' }}>3D</button>
                    )}
                    <button onClick={() => handleDeleteStored(item)} title={`Delete ${item.name}`} aria-label={`Delete ${item.name}`}
                      style={{ fontSize: 10, background: 'none', border: 'none', color: C.red, cursor: 'pointer' }}>🗑</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Model list */}
        {models.length > 0 && (
          <div>
            <label style={lbl}>Model</label>
            <select value={activeModelId} onChange={e => {
              setActiveModelId(e.target.value); setDepthIdx(0);
              const next = models.find(m => m.id === e.target.value);
              if (next && !next.model.columns.includes(sliceProp)) setSliceProp(defaultProp(next));
            }} style={selStyle}>
              {models.map(m => <option key={m.id} value={m.id}>{m.name}{m.sample ? ' (sample, not saved)' : ''}</option>)}
            </select>
            {active && (
              <div style={{ fontSize: 9, color: C.text3, fontFamily: 'monospace', marginTop: 4, lineHeight: 1.6 }}>
                {active.model.nCells.toLocaleString()} cells · {active.model.format}{active.model.geometryKind ? ` · ${active.model.geometryKind}` : ''}<br />
                depth 0–{active.model.depthRange[1]}m · DOI {active.model.doi}m
                {active.model.rmsPercent != null && <> · RMS {active.model.rmsPercent}%</>}<br />
                {activeIsLocal ? <span style={{ color: C.amber }}>local coordinates (not on the map yet)</span>
                  : <>CRS {active.georef?.crs || 'EPSG:4326'}{active.georef?.derived?.azimuth_deg !== undefined && <> · az {Number(active.georef.derived.azimuth_deg).toFixed(1)}°</>}</>}
              </div>
            )}
            {active && active.model.nIterations > 1 && geo.sourceOf(active.id) && (
              <div style={{ marginTop: 6 }}>
                <label style={lbl}>Iteration</label>
                <select value={active.model.iteration ?? ''} onChange={e => changeIteration(parseInt(e.target.value))} style={selStyle} disabled={busy}>
                  {active.model.iterations.map(it => <option key={it.n} value={it.n}>{iterationLabel(it)}</option>)}
                </select>
              </div>
            )}
            {active && (
              <button onClick={() => geo.open(active)}
                style={{ marginTop: 6, width: '100%', fontSize: 11, padding: '6px', borderRadius: 8, background: activeIsLocal ? 'rgba(251,191,36,0.15)' : C.bg4, color: activeIsLocal ? C.amber : C.text1, border: `1px solid ${activeIsLocal ? 'rgba(251,191,36,0.35)' : C.border}`, fontWeight: 600, cursor: 'pointer' }}>
                📍 {activeIsLocal ? 'Define coordinates' : 'Edit georeference'}
              </button>
            )}
          </div>
        )}

        {/* Depth slice */}
        {active && depths.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.text2, textTransform: 'uppercase', marginBottom: 6 }}>Depth Slice</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <select value={sliceProp} onChange={e => { setSliceProp(e.target.value); showSlice(depthIdx, e.target.value); }} style={{ ...selStyle, flex: 1 }}>
                {availableProps.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <button onClick={() => setPlaying(p => !p)} title={playing ? 'Stop animation' : 'Animate through depths'}
                style={{ width: 32, borderRadius: 6, border: `1px solid ${playing ? 'rgba(251,191,36,0.4)' : C.border}`, background: playing ? 'rgba(251,191,36,0.15)' : C.bg4, color: playing ? C.amber : C.text1, cursor: 'pointer', fontSize: 12 }}>
                {playing ? '⏸' : '▶'}
              </button>
            </div>
            {isDiff && (
              <div style={{ marginBottom: 6 }}>
                <TimelapseDateSlider C={C} model={active.model} property={sliceProp}
                  onChange={(p) => { setSliceProp(p); showSlice(depthIdx, p); }} />
              </div>
            )}
            <label style={lbl}>
              Depth — <span style={{ color: C.accent, fontFamily: 'monospace' }}>{depths[depthIdx]} m</span> ({depthIdx + 1}/{depths.length})
            </label>
            <input type="range" min={0} max={depths.length - 1} step={1} value={depthIdx}
              onChange={e => { const idx = parseInt(e.target.value); setDepthIdx(idx); showSlice(idx, sliceProp); }}
              style={{ width: '100%', accentColor: C.accent }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={() => showSlice(depthIdx, sliceProp)}
                style={{ flex: 1, fontSize: 10, padding: '5px', borderRadius: 6, background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                Show Slice
              </button>
              <button onClick={() => { setPlaying(false); sliceShownRef.current = false; onSliceLayer(null); }}
                style={{ fontSize: 10, padding: '5px 8px', borderRadius: 6, background: C.bg4, color: C.text2, border: `1px solid ${C.border}`, cursor: 'pointer' }}>
                Hide
              </button>
            </div>
          </div>
        )}

        {/* Time-lapse: difference of the active model (A, earlier) and a second one (B, later) */}
        {active && (models.length > 1 || compareStored.length > 0) && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.text2, textTransform: 'uppercase', marginBottom: 6 }}>Compare (time-lapse)</div>
            <label style={lbl} htmlFor="compare-b">Model B (later) − active model A</label>
            <select id="compare-b" value={compareId} onChange={e => setCompareId(e.target.value)} style={selStyle} disabled={diff.busy}>
              <option value="">Select a second model…</option>
              {models.filter(m => m.id !== active.id && m.model.format !== 'timelapse-diff').map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              {compareStored.map(i => <option key={`stored:${i.id}`} value={`stored:${i.id}`}>{i.name} (stored)</option>)}
            </select>
            <input type="text" value={compareName} placeholder="Name (optional)" onChange={e => setCompareName(e.target.value)}
              style={{ ...selStyle, marginTop: 4, boxSizing: 'border-box' }} disabled={diff.busy} />
            <button disabled={!compareId || diff.busy}
              onClick={runCompare}
              style={{ marginTop: 6, width: '100%', fontSize: 11, padding: '7px', borderRadius: 8, background: 'rgba(248,113,113,0.12)', color: C.red, border: '1px solid rgba(248,113,113,0.3)', fontWeight: 600, cursor: !compareId || diff.busy ? 'not-allowed' : 'pointer', opacity: !compareId ? 0.6 : 1 }}>
              {diff.busy ? '⏳ Differencing…' : '⇄ Compare'}
            </button>
            {diff.busy && diff.job && (
              <div style={{ marginTop: 6 }}>
                <div style={{ height: 4, borderRadius: 2, background: C.bg4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.round((diff.job.progress || 0) * 100)}%`, height: '100%', background: C.accent }} />
                </div>
                <div style={{ fontSize: 9, color: C.text3, marginTop: 2 }}>{diff.job.message || diff.job.status}
                  <button onClick={diff.cancel} style={{ marginLeft: 6, fontSize: 9, background: 'none', border: 'none', color: C.red, cursor: 'pointer' }}>cancel</button>
                </div>
              </div>
            )}
            {diff.error && <div role="alert" style={{ fontSize: 10, color: C.red, padding: '5px 8px', marginTop: 6, background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {diff.error}</div>}
            {isDiff && active.model.diff && (
              <div style={{ fontSize: 9, color: C.text3, fontFamily: 'monospace', marginTop: 6, lineHeight: 1.6 }}>
                {active.model.diff.n_cells.toLocaleString()} cells · {active.model.diff.resampled} resampled · {active.model.diff.dropped} dropped<br />
                % change {Number(active.model.diff.pct_change_min).toFixed(1)} … {Number(active.model.diff.pct_change_max).toFixed(1)} (mean {Number(active.model.diff.pct_change_mean).toFixed(1)})
              </div>
            )}
          </div>
        )}

        {/* Depth of investigation: global mask + threshold */}
        {active && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: C.text2, textTransform: 'uppercase', marginBottom: 6 }}>Depth of Investigation</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: C.text2, cursor: 'pointer' }}>
              <input type="checkbox" checked={doi.mask} onChange={e => doi.setMask(e.target.checked)} style={{ accentColor: C.accent }} />
              DOI mask (hide / grey cells below DOI)
            </label>
            <label style={{ ...lbl, marginTop: 6 }}>
              Sensitivity threshold — <span style={{ color: C.accent, fontFamily: 'monospace' }}>{doi.threshold.toFixed(2)}</span> of column max
            </label>
            <input type="range" min={0.01} max={0.5} step={0.01} value={doi.threshold}
              onChange={e => doi.setThreshold(parseFloat(e.target.value))} style={{ width: '100%', accentColor: C.accent }} />
            <div style={{ fontSize: 9, color: C.text3, fontFamily: 'monospace', marginTop: 2 }}>
              {doiMean === null ? 'no DOI available'
                : doiProfile.source === 'sensitivity' ? `mean DOI ${doiMean.toFixed(1)} m (from sensitivity)`
                  : `DOI ${doiMean.toFixed(1)} m (estimate: no sensitivity column)`}
            </div>
          </div>
        )}

        {active && <DemSurfaceToggle C={C} />}

        {/* QC themes + section */}
        {active && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={generateQcThemes}
              style={{ fontSize: 11, padding: '7px', borderRadius: 8, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, fontWeight: 600, cursor: 'pointer' }}>
              🧪 Generate QC Maps ({availableProps.length})
            </button>
            <button onClick={() => onOpenSection(active.id)}
              style={{ fontSize: 11, padding: '7px', borderRadius: 8, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, fontWeight: 600, cursor: 'pointer' }}>
              📐 Open Filled Section
            </button>
            <button onClick={() => setShowThemeMaps(v => !v)}
              style={{ fontSize: 11, padding: '7px', borderRadius: 8, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, fontWeight: 600, cursor: 'pointer' }}>
              🗺 Theme Maps (layer properties)
            </button>
            {onOpenDataFit && active.model?.dataFit && (
              <button onClick={() => onOpenDataFit(active)} data-testid="inversion-data-fit"
                style={{ fontSize: 11, padding: '7px', borderRadius: 8, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, fontWeight: 600, cursor: 'pointer' }}>
                📈 Data fit (measured / calculated / misfit)
              </button>
            )}
            <button onClick={() => setShowQcCharts(true)}
              style={{ fontSize: 11, padding: '7px', borderRadius: 8, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, fontWeight: 600, cursor: 'pointer' }}>
              📊 QC Charts
            </button>
          </div>
        )}

        <SaveModelDialog open={!!pending} name={pending?.model?.name} fileName={pending?.fileName} projectId={stored.projectId}
          onSave={(pid) => finishImport(pending, pid)} onSkip={() => finishImport(pending, null)} />

        {geo.target && (
          <GeorefDialog
            title={`Define coordinates — ${geo.target.name}`}
            electrodes={(geo.target.model.electrodes || []).map(({ id, x, y, z }) => ({ id, x, y: y || 0, z: z || 0 }))}
            geometryKind={geo.target.model.geometryKind}
            cells={['grid', 'mesh3d'].includes(geo.target.model.geometryKind) ? geo.target.model.cells : []}
            initialGeoref={geo.target.georef}
            xRange={geo.target.model.xRange || null}
            crsGuess={guessCrs((geo.target.model.electrodes || []).map(e => e.x), (geo.target.model.electrodes || []).map(e => e.y || 0))}
            onSave={geo.save}
            onClose={geo.close}
          />
        )}

        {showThemeMaps && active && (
          <ThemeMapPanel entry={active} onAddLayer={onAddQcLayer} onClose={() => setShowThemeMaps(false)} />
        )}

        {showQcCharts && active && (
          <QCChartsModal model={active.model} name={active.name} projectId={stored.projectId} storedId={active.backendId || null} onClose={() => setShowQcCharts(false)} />
        )}

        {models.length === 0 && !error && (
          <div style={{ fontSize: 10, color: C.text2, lineHeight: 1.6 }}>
            Import an EarthImager 1D/2D/3D result (.out, .mod, InvRes .dat) or an EarthImager/RES2DINV export (.xyz, .inv, .dat). If the file has no map coordinates you are asked for the start and end points (or an origin and azimuth, or tie points); a line drawn on the map also works.
          </div>
        )}
      </div>
    </div>
  );
}
